import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Divider,
  Stack,
  Tooltip,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

/**
 * Get avatar color from user name
 */
const getAvatarColor = (name) => {
  const colors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'];
  if (!name) return colors[0];
  const charCode = name.charCodeAt(0);
  return colors[charCode % colors.length];
};

/**
 * Get initials from name
 */
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

/**
 * Composant récapitulatif de l'exécution d'une pénalité
 * 
 * Affiche les détails de comment la pénalité a été collectée :
 * - Montant total collecté
 * - Sources utilisées (deposits, payouts)
 * - Opérations Stripe effectuées
 * - Statut de la compensation victime
 */
const PenaltyExecutionSummary = ({ penalty, dispute }) => {
  const { t } = useTranslation('disputes');
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes('admin');
  
  if (!penalty || !penalty.execution_metadata) {
    return null;
  }

  const getProfilePictureUrl = (user) => {
    if (!user?.profilePicture) return '';

    const url = String(user.profilePicture);
    const simpleHash = url.split('').reduce((acc, char) => {
      return ((acc << 5) - acc) + char.charCodeAt(0) | 0;
    }, 0);

    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${Math.abs(simpleHash).toString(36)}`;
  };

  const resolveUserFromDispute = (userId) => {
    if (!dispute || !userId) return null;

    const candidates = [
      dispute.reporter,
      dispute.reportedUser,
      dispute.reservation?.renter,
      dispute.reservation?.listing?.owner
    ].filter(Boolean);

    const found = candidates.find((u) => u?.id === userId);
    if (found) return found;

    // Fallbacks: sometimes only ids are present
    if (dispute.reservation?.renter_id === userId) return dispute.reservation?.renter || null;
    if (dispute.reservation?.listing?.ownerId === userId) return dispute.reservation?.listing?.owner || null;
    if (dispute.reservation?.listing?.owner_id === userId) return dispute.reservation?.listing?.owner || null;

    return null;
  };

  const victimUserId = penalty.execution_metadata?.victim_compensation?.victim_user_id;
  const victimUser = resolveUserFromDispute(victimUserId);

  // Important: `penalty.user` est parfois partiel (sans profilePicture).
  // On fusionne avec l'utilisateur résolu depuis `dispute` afin de récupérer l'avatar réel.
  const sanctionedUserFromDispute = resolveUserFromDispute(penalty.user_id);
  const sanctionedUser = {
    ...(penalty.user || {}),
    ...(sanctionedUserFromDispute || {})
  };

  const { execution_metadata, amount, currency, status, applied_at } = penalty;
  const {
    total_collected = 0,
    amount_remaining = 0,
    collection_details = [],
    stripe_operations = [],
    victim_compensation = {},
    summary = {},
    executed_at,
    collection_currency
  } = execution_metadata;

  const isFullyCollected = status === 'applied' || status === 'collected';
  const isPartial = status === 'pending_collection';
  const isFailed = status === 'failed';

  // Détermine la devise de collection (EUR pour Stripe) vs devise de pénalité (DZD, etc.)
  const stripeCurrency = collection_currency || collection_details?.[0]?.currency || 'EUR';

  // Format amount avec devise personnalisable
  const formatAmount = (amt, curr = currency) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: curr || 'EUR'
    }).format(amt);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: fr });
  };

  // Get status color and icon
  const getStatusDisplay = () => {
    if (isFullyCollected) {
      return {
        color: 'success',
        icon: <CheckCircleIcon />,
        label: t('penalties.status.fullyColl'),
        severity: 'success'
      };
    }
    if (isPartial) {
      return {
        color: 'warning',
        icon: <WarningIcon />,
        label: t('penalties.status.partial'),
        severity: 'warning'
      };
    }
    if (isFailed) {
      return {
        color: 'error',
        icon: <ErrorIcon />,
        label: t('penalties.status.failed'),
        severity: 'error'
      };
    }
    return {
      color: 'default',
      icon: <InfoIcon />,
      label: t('penalties.execution.pending'),
      severity: 'info'
    };
  };

  const statusDisplay = getStatusDisplay();

  const translateOperationType = (type) => {
    if (!type) return '';
    return t(`penalties.execution.stripeOperationTypes.${type}`, type);
  };

  const translateOperationStatus = (statusValue) => {
    if (!statusValue) return '';
    return t(`penalties.execution.operationStatuses.${statusValue}`, statusValue);
  };

  const translateVictimCompensationStatus = (statusValue) => {
    if (!statusValue) return '';
    return t(`penalties.execution.victimCompensation.${statusValue}`, statusValue);
  };

  const translateCompensationMethod = (methodValue) => {
    if (!methodValue) return '';
    return t(`penalties.execution.compensationMethods.${methodValue}`, methodValue);
  };

  const translateVictimNote = (note) => {
    if (!note) return '';

    // ✅ Success note
    if (note === 'Victim compensation transferred via Stripe Connect') {
      return t('penalties.execution.victimNotes.transferredViaStripeConnect');
    }

    // ✅ Auto-transfer not possible (generic backend message)
    if (note.startsWith('Automatic transfer not possible')) {
      return t('penalties.execution.victimNotes.automaticTransferNotPossible');
    }

    // ✅ Stripe transfer failures – match all error patterns
    const isStripeFailure =
      /stripe transfer failed/i.test(note) ||
      /stripe.*error/i.test(note);

    if (isStripeFailure) {
      const lower = note.toLowerCase();

      if (lower.includes('insufficient') && lower.includes('funds')) {
        return t('penalties.execution.victimNotes.stripeInsufficientFunds');
      }
      if (lower.includes('no such destination') || lower.includes('account not found')) {
        return t('penalties.execution.victimNotes.stripeAccountNotFound');
      }
      if (lower.includes('not connected') || lower.includes('is not connected')) {
        return t('penalties.execution.victimNotes.stripeAccountNotConnected');
      }
      if (lower.includes('capability') || lower.includes('capabilities')) {
        return t('penalties.execution.victimNotes.stripeMissingCapability');
      }
      if (lower.includes('account is disabled') || lower.includes('account has been disabled')) {
        return t('penalties.execution.victimNotes.stripeAccountDisabled');
      }
      if (lower.includes('currency') && lower.includes('not supported')) {
        return t('penalties.execution.victimNotes.stripeCurrencyNotSupported');
      }
      if (lower.includes('kyc') || lower.includes('identity') || lower.includes('verification required')) {
        return t('penalties.execution.victimNotes.stripeKycRequired');
      }
      if (lower.includes('rate limit') || lower.includes('too many requests')) {
        return t('penalties.execution.victimNotes.stripeRateLimit');
      }
      // Generic Stripe failure (admin sees raw, others see friendly message)
      if (isAdmin) return note;
      return t('penalties.execution.victimNotes.stripeTransferFailed');
    }

    // ✅ Unrecognized note: admin sees raw, others see generic message
    if (isAdmin) return note;
    return t('penalties.execution.victimNotes.genericTransferIssue');
  };

  const getPersonalizedBlockedMessage = (reason, accountStatus) => {
    if (!reason) return null;
    
    // Determine the action message based on reason
    let messageKey = null;
    if (reason === 'Account onboarding not completed') {
      messageKey = 'penalties.execution.blockedMessages.onboardingNotCompleted';
    } else if (reason === 'Payouts not enabled on this account') {
      messageKey = 'penalties.execution.blockedMessages.payoutsNotEnabled';
    } else if (reason === 'Missing transfers capability') {
      messageKey = 'penalties.execution.blockedMessages.missingTransfersCapability';
    } else if (reason.startsWith('Account disabled:')) {
      messageKey = 'penalties.execution.blockedMessages.accountDisabled';
      const disabledReason = reason.replace('Account disabled: ', '');
      return (
        <Box sx={{ bgcolor: 'warning.lighter', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'warning.dark' }}>
            {t(messageKey, { reason: disabledReason })}
          </Typography>
          {accountStatus && (
            <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'warning.main' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {t('penalties.execution.accountStatusLabel')}
              </Typography>
              <Typography variant="caption" display="block">
                • {t('penalties.execution.detailsSubmitted')}: {accountStatus.details_submitted ? '✅' : '❌'}
              </Typography>
              <Typography variant="caption" display="block">
                • {t('penalties.execution.chargesEnabled')}: {accountStatus.charges_enabled ? '✅' : '❌'}
              </Typography>
              <Typography variant="caption" display="block">
                • {t('penalties.execution.payoutsEnabled')}: {accountStatus.payouts_enabled ? '✅' : '❌'}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
    
    if (!messageKey) return null;
    
    return (
      <Box sx={{ bgcolor: 'warning.lighter', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'warning.dark' }}>
          {t(messageKey)}
        </Typography>
        {accountStatus && (
          <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'warning.main' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {t('penalties.execution.accountStatusLabel')}
            </Typography>
            <Typography variant="caption" display="block">
              • {t('penalties.execution.detailsSubmitted')}: {accountStatus.details_submitted ? '✅' : '❌'}
            </Typography>
            <Typography variant="caption" display="block">
              • {t('penalties.execution.chargesEnabled')}: {accountStatus.charges_enabled ? '✅' : '❌'}
            </Typography>
            <Typography variant="caption" display="block">
              • {t('penalties.execution.payoutsEnabled')}: {accountStatus.payouts_enabled ? '✅' : '❌'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Group collection details by source type
  const depositCollections = collection_details.filter(c => c.source === 'deposit');
  const payoutCollections = collection_details.filter(c => c.source === 'payout_deduction');

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('penalties.execution.summaryTitle')}
            </Typography>
          </Box>
          <Chip
            icon={statusDisplay.icon}
            label={statusDisplay.label}
            color={statusDisplay.color}
            size="medium"
          />
        </Box>

        <Divider />

        {/* Status Alert */}
       <Alert severity={statusDisplay.severity} icon={statusDisplay.icon}>
          <Box>
            {isFullyCollected ? (
              <Typography variant="body2">
                ✅ {t('penalties.execution.fullyColl')} {formatDate(applied_at || executed_at)}
              </Typography>
            ) : isPartial ? (
              <Typography variant="body2">
                {formatAmount(total_collected,'EUR')} {t('penalties.execution.collected')} {formatAmount(amount)} ({t('penalties.execution.remaining')} {formatAmount(amount_remaining,'EUR')})
              </Typography>
            ) : isFailed ? (
              <Typography variant="body2">
                ❌ {t('penalties.execution.failed')}
              </Typography>
            ) : null}
          </Box>
        </Alert>

        {/* Parties impliquées */}
        <Box sx={{ p: 2, bgcolor: 'primary.50', borderBottom: '2px solid', borderColor: 'primary.light' }}>
          <Typography variant="overline" color="text.secondary" gutterBottom display="block" sx={{ mb: 1.5 }}>
            👥 Parties impliquées
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap' }}>
            {/* Utilisateur sanctionné */}
            <Tooltip title={`${t('penalties.parties.sanctioned')} (ID: ${penalty.user_id?.substring(0, 8)})`}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 140 }}>
                <Avatar
                  src={getProfilePictureUrl(sanctionedUser) || undefined}
                  sx={{
                    bgcolor: 'primary.main',
                    width: 48,
                    height: 48,
                    fontWeight: 'bold',
                    fontSize: '1.125rem'
                  }}
                >
                  {sanctionedUser?.name ? getInitials(sanctionedUser.name) : <PersonIcon fontSize="small" />}
                </Avatar>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight="bold" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '1rem' }}>
                    {sanctionedUser?.name || sanctionedUser?.email || t('penalties.messages.userFallback')}
                  </Typography>
                  <Chip
                    label={t('penalties.parties.sanctioned')}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'error.main',
                      borderWidth: 2,
                      color: 'error.main',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                      width: 'fit-content'
                    }}
                  />
                </Stack>
              </Stack>
            </Tooltip>

            {/* Flèche */}
            <ArrowForwardIcon sx={{ color: 'inherit', fontSize: 24, opacity: 0.6 }} />

            {/* Bénéficiaire */}
            {penalty.execution_metadata?.victim_compensation?.victim_user_id ? (
              <Tooltip title={`${victimUser?.name || victimUser?.email || t('penalties.messages.victimFallback')} — ${t('penalties.parties.beneficiary')} (ID: ${penalty.execution_metadata.victim_compensation.victim_user_id?.substring(0, 8)})`}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 140 }}>
                  <Avatar
                    src={getProfilePictureUrl(victimUser) || undefined}
                    sx={{
                      bgcolor: 'success.main',
                      width: 48,
                      height: 48,
                      fontWeight: 'bold',
                      fontSize: '1.125rem'
                    }}
                  >
                    {victimUser?.name ? getInitials(victimUser.name) : <PersonIcon fontSize="small" />}
                  </Avatar>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight="bold" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '1rem' }}>
                      {victimUser?.name || victimUser?.email || t('penalties.messages.victimFallback')}
                    </Typography>
                    <Chip
                      label={t('penalties.parties.beneficiary')}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'success.main',
                        borderWidth: 2,
                        color: 'success.main',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                        width: 'fit-content'
                      }}
                    />
                  </Stack>
                </Stack>
              </Tooltip>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                {t('penalties.parties.noBeneficiary')}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {t('penalties.execution.penaltyAmount')}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatAmount(amount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ bgcolor: 'success.lighter' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {t('penalties.execution.collectedAmount')}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.dark' }}>
                  {formatAmount(total_collected, stripeCurrency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ bgcolor: amount_remaining > 0 ? 'warning.lighter' : 'grey.100' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {t('penalties.execution.toBeCollected')}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: amount_remaining > 0 ? 'warning.dark' : 'text.secondary' }}>
                  {formatAmount(amount_remaining,'EUR')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Collection Details */}
        {collection_details.length > 0 && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoneyIcon /> {t('penalties.execution.collectSources')}
            </Typography>

            {/* Deposits */}
            {depositCollections.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                  💰 {t('penalties.execution.capturedDeposits')} ({depositCollections.length})
                </Typography>
                <Table size="small">
                  <TableBody>
                    {depositCollections.map((detail, index) => (
                      <React.Fragment key={index}>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell>
                            <strong>{t('penalties.execution.depositNumber')}{index + 1}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={translateOperationStatus(detail.status || 'succeeded')} 
                              color="success" 
                              size="small" 
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{t('penalties.execution.amountCollected')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                            {formatAmount(detail.amount, detail.currency || stripeCurrency)}
                          </TableCell>
                        </TableRow>
                        {detail.stripe_payment_intent_id && (
                          <TableRow>
                            <TableCell>{t('penalties.execution.stripePaymentIntent')}</TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {detail.stripe_payment_intent_id}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {detail.method && (
                          <TableRow>
                            <TableCell>{t('penalties.execution.method')}</TableCell>
                            <TableCell align="right">
                              <Chip label={detail.method} size="small" variant="outlined" />
                            </TableCell>
                          </TableRow>
                        )}
                        {index < depositCollections.length - 1 && (
                          <TableRow>
                            <TableCell colSpan={2}><Divider /></TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Payouts */}
            {payoutCollections.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'warning.main' }}>
                  📄 {t('penalties.execution.deductedPayouts')} ({payoutCollections.length})
                </Typography>
                <Table size="small">
                  <TableBody>
                    {payoutCollections.map((detail, index) => (
                      <React.Fragment key={index}>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell>
                            <strong>{t('penalties.execution.payoutNumber')}{index + 1}</strong>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={translateOperationStatus(detail.status || 'deducted')} 
                              color="warning" 
                              size="small" 
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{t('penalties.execution.deductedAmount')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'warning.main' }}>
                            {formatAmount(detail.deducted_amount || detail.amount, detail.currency || stripeCurrency)}
                          </TableCell>
                        </TableRow>
                        {detail.payout_id && (
                          <TableRow>
                            <TableCell>{t('penalties.execution.payoutId')}</TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {detail.payout_id.substring(0, 20)}...
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {detail.new_net_amount !== undefined && (
                          <TableRow>
                            <TableCell>{t('penalties.execution.newNetAmount')}</TableCell>
                            <TableCell align="right">
                              {formatAmount(detail.new_net_amount, detail.currency || stripeCurrency)}
                            </TableCell>
                          </TableRow>
                        )}
                        {index < payoutCollections.length - 1 && (
                          <TableRow>
                            <TableCell colSpan={2}><Divider /></TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}

        {/* Stripe Operations */}
        {stripe_operations.length > 0 && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceIcon /> {t('penalties.execution.stripeOpsCount')} ({stripe_operations.length})
            </Typography>
            <Table size="small">
              <TableBody>
                {stripe_operations.map((op, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                    <TableCell>
                      <Chip 
                        label={translateOperationType(op.type)} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {op.stripe_id}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatAmount(op.amount, op.currency || stripeCurrency)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={translateOperationStatus(op.status)} 
                        color={op.status === 'succeeded' ? 'success' : op.status === 'failed' ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Victim Compensation */}
        {victim_compensation && victim_compensation.status && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon /> {t('penalties.execution.victimCompensationSection')}
            </Typography>
            <Alert 
              severity={
                victim_compensation.status === 'transferred' ? 'success' :
                victim_compensation.status === 'pending' ? 'warning' :
                victim_compensation.status === 'requires_manual_action' ? 'info' :
                'error'
              }
            >
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>{t('penalties.execution.statut')}</strong></TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={translateVictimCompensationStatus(victim_compensation.status)} 
                        color={
                          victim_compensation.status === 'transferred' ? 'success' :
                          victim_compensation.status === 'pending' ? 'warning' : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  {victim_compensation.amount && (
                    <TableRow>
                      <TableCell>{t('penalties.execution.amount')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatAmount(victim_compensation.amount)}
                      </TableCell>
                    </TableRow>
                  )}
                  {victim_compensation.compensation_method && (
                    <TableRow>
                      <TableCell>{t('penalties.execution.compensationMethod')}</TableCell>
                      <TableCell align="right">
                        {translateCompensationMethod(victim_compensation.compensation_method)}
                      </TableCell>
                    </TableRow>
                  )}
                  {victim_compensation.stripe_transfer_id && (
                    <TableRow>
                      <TableCell>{t('penalties.execution.stripeTransferId')}</TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {victim_compensation.stripe_transfer_id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Afficher les détails de blocage pour action manuelle requise */}
                  {victim_compensation.status === 'requires_manual_action' && victim_compensation.transfer_blocked_reason && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        {getPersonalizedBlockedMessage(victim_compensation.transfer_blocked_reason, victim_compensation.account_status)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* User-friendly next-steps banner for requires_manual_action (non-admin) */}
                  {victim_compensation.status === 'requires_manual_action' && !isAdmin && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            {t('penalties.execution.victimNotes.manualActionNextSteps')}
                          </Typography>
                        </Alert>
                      </TableCell>
                    </TableRow>
                  )}

                  {victim_compensation.note && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="caption" color="text.secondary">
                          {translateVictimNote(victim_compensation.note)}
                        </Typography>
                        {/* Raw Stripe error detail — admin only */}
                        {isAdmin && /stripe transfer failed/i.test(victim_compensation.note) && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all', fontSize: '0.7rem' }}
                          >
                            ℹ️ {victim_compensation.note}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Alert>
          </Box>
        )}

        {/* Execution Timestamp */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <AccessTimeIcon fontSize="small" />
          <Typography variant="caption">
            {t('penalties.execution.executedOn')} {formatDate(executed_at || applied_at)}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

export default PenaltyExecutionSummary;
